// @flow
import _ from 'lodash';
import React, { useState } from 'react';
import { Icon } from '@shopify/polaris';
import { ClipboardMinor, TickMinor } from '@shopify/polaris-icons';
import { useI18n } from '@shopify/react-i18n';
import GA4PropertySelector from './property-selector';

/**
 * Find the active property (marked with isActive: true)
 */
const getActiveProperty = (info) => {
  if (!info || !info.properties) return {};
  const property = info.properties.find(p => p.isActive === true);
  return property || {};
};

/**
 * Get the measurement ID from the active property's first valid dataStream
 */
const getActivePropertyMeasurementId = (info) => {
  const property = getActiveProperty(info);
  if (!property.dataStreams) return '';
  const stream = property.dataStreams.find(ds => ds !== null && ds.webStreamData);
  return _.get(stream, 'webStreamData.measurementId', '');
};

type Props = {
  googleInformation: Object,
  onConnectProperty?: (selectedPropertyName: string) => Promise<void>,
};

const GA4AccountInfo = ({ googleInformation, onConnectProperty }: Props) => {
  const [i18n] = useI18n();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const measurementId = getActivePropertyMeasurementId(googleInformation);
  const activeProperty = getActiveProperty(googleInformation);
  const properties = _.get(googleInformation, 'properties', []);
  const hasMultipleProperties = properties.length > 1;

  const handleConnectProperty = async (selectedPropertyName) => {
    if (onConnectProperty) {
      return onConnectProperty(selectedPropertyName);
    }
  };

  return (
    <div className="ga4-account-info">
      <div className="ga4-account-info__row">
        <p className="ga4-account-info__label">
          {i18n.translate('AdminHTML.GoogleAnalytics4.ConnectAccounts.toggleData.connectGoogleAccount')}
        </p>
        <p className="ga4-account-info__value">
          {_.get(googleInformation, 'account.email', '')}
        </p>
      </div>
      <div className="ga4-account-info__row">
        <p className="ga4-account-info__label">
          {i18n.translate('AdminHTML.GoogleAnalytics4.ConnectAccounts.toggleData.propertyName')}
        </p>
        <p className="ga4-account-info__value">
          {activeProperty.displayName || ''}
        </p>
      </div>
      <div className="ga4-account-info__row">
        <p className="ga4-account-info__label">
          {i18n.translate('AdminHTML.GoogleAnalytics4.ConnectAccounts.toggleData.propertyId')}
        </p>
        <p className="ga4-account-info__value">
          {measurementId}
          <span
            className="ga4-account-info__copy"
            onClick={() => handleCopy(measurementId)}
            title={isCopied ? 'Copied' : 'Copy'}
          >
            <Icon source={isCopied ? TickMinor : ClipboardMinor} color={isCopied ? 'success' : 'subdued'} />
          </span>
        </p>
      </div>
      {hasMultipleProperties && (
        <GA4PropertySelector
          properties={properties}
          currentPropertyName={activeProperty.property || activeProperty.name || ''}
          onConnect={handleConnectProperty}
        />
      )}
    </div>
  );
};

export default GA4AccountInfo;
